/**
 * Strava OAuth 2.0 & REST Client Integration Module
 * Handles OAuth handshake, token refresh, activity queries, telemetry streams, and outbound TCX uploads.
 *
 * @module integrations/strava
 */

import { generateTCX } from './tcx.js';
import { calculateWorkoutZoneSeconds } from '../telemetry/index.js';
import { getWorkoutSufferScore } from '../performance/index.js';
import { store } from '../../store.js';

/**
 * Deterministically converts a numeric Strava activity ID to a valid UUID v4 format.
 * @param {number|string} stravaId
 * @returns {string} UUID string
 */
export function stravaIdToUUID(stravaId) {
  if (!stravaId) {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : '00000000-0000-4000-8000-000000000000';
  }
  const num = parseInt(stravaId, 10);
  if (isNaN(num)) {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : '00000000-0000-4000-8000-000000000000';
  }
  const hex = num.toString(16).padStart(12, '0');
  return `00000000-0000-4000-8000-${hex.substring(0, 12)}`;
}

/**
 * Computes total running & transition distance in kilometers for a workout.
 * @param {Object} workout
 * @returns {number} Distance in km
 */
export function getWorkoutTotalDistance(workout) {
  if (!workout) return 0;

  if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
    let totalMeters = 0;

    workout.stages.forEach(st => {
      const typeLower = (st.type || st.stage_type || '').toLowerCase();
      const nameLower = (st.stationName || st.name || '').toLowerCase();
      const isRun = typeLower === 'run' || typeLower === 'running' || typeLower === 'cardio';
      const isRox = typeLower === 'roxzone' || nameLower.includes('rox');

      if ((isRun || isRox) && typeLower !== 'rest') {
        const distVal = st.distance !== undefined && st.distance !== null ? st.distance : st.distance_meters;
        if (distVal && parseFloat(distVal) > 0) {
          totalMeters += parseFloat(distVal);
        }
      }
    });

    return totalMeters > 0 ? Math.round((totalMeters / 1000) * 100) / 100 : 0;
  }

  if (workout.type === 'Run' && workout.distance && parseFloat(workout.distance) > 0) {
    return Math.round(parseFloat(workout.distance) * 100) / 100;
  }

  return 0;
}

export function formatTotalDuration(duration) {
  if (!duration || !Number.isFinite(duration) || duration <= 0) return '00:00';
  const totalSecs = Math.round(duration * 60);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatStageDuration(min, sec) {
  const m = Number.isFinite(min) ? Math.max(0, Math.floor(min)) : 0;
  const s = Number.isFinite(sec) ? Math.max(0, Math.round(sec)) : 0;
  const totalSecs = m * 60 + s;
  const finalMin = Math.floor(totalSecs / 60);
  const finalSec = totalSecs % 60;
  return `${String(finalMin).padStart(2, '0')}:${String(finalSec).padStart(2, '0')}`;
}

/**
 * Builds the Strava OAuth 2.0 authorization URL.
 *
 * @param {string} clientId
 * @param {string} redirectUri
 * @param {string} [scope='activity:write,activity:read_all']
 * @returns {string} URL string
 */
export function getStravaAuthUrl(clientId, redirectUri, scope = 'activity:write,activity:read_all') {
  if (!clientId) throw new Error('clientId is required for Strava OAuth');
  const cleanRedirect = redirectUri || (typeof window !== 'undefined' ? window.location.origin : '');
  return `https://www.strava.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(cleanRedirect)}&response_type=code&scope=${encodeURIComponent(scope)}`;
}

/**
 * Exchanges a temporary authorization code for Strava access and refresh tokens.
 *
 * @param {string} code - Temporary auth code from OAuth callback.
 * @param {string} clientId - Strava App Client ID.
 * @param {string} clientSecret - Strava App Client Secret.
 * @param {string} redirectUri - Registered OAuth redirect URI.
 * @returns {Promise<Object>} Token response payload.
 */
export async function exchangeStravaToken(code, clientId, clientSecret, redirectUri) {
  if (!clientId || !clientSecret || !code) {
    throw new Error('code, clientId, and clientSecret are required for token exchange.');
  }

  const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: clientId,
      client_secret: clientSecret,
      code: code,
      grant_type: 'authorization_code',
      redirect_uri: redirectUri
    })
  });

  if (!response.ok) {
    let errMsg = `HTTP error ${response.status}`;
    try {
      const errData = await response.json();
      if (errData.message) errMsg = errData.message;
    } catch (_) {}
    throw new Error(errMsg);
  }

  return await response.json();
}

/**
 * Refreshes an expired or expiring Strava access token if within 300s of expiry.
 *
 * @param {Object} tokenState - Object containing { accessToken, refreshToken, expiresAt }.
 * @param {string} clientId - Strava App Client ID.
 * @param {string} clientSecret - Strava App Client Secret.
 * @returns {Promise<Object>} Updated token state.
 */
export async function refreshStravaTokenIfExpired(tokenState, clientId, clientSecret) {
  if (!tokenState || !tokenState.refreshToken) {
    return tokenState;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expiresAt = tokenState.expiresAt || 0;
  const secondsRemaining = expiresAt - currentTime;

  // Skip refresh if token is still valid for > 5 minutes
  if (expiresAt && secondsRemaining > 300) {
    return tokenState;
  }

  const resolvedClientId = String(clientId ?? tokenState.clientId ?? (store && store.strava ? store.strava.clientId : '') ?? '').trim();
  const resolvedClientSecret = String(clientSecret ?? tokenState.clientSecret ?? (store && store.strava ? store.strava.clientSecret : '') ?? '').trim();

  if (!resolvedClientId || !resolvedClientSecret) {
    throw new Error('Strava Client ID or Client Secret is missing. Please enter and save your Strava credentials in Settings.');
  }

  const response = await fetch('https://www.strava.com/api/v3/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: resolvedClientId,
      client_secret: resolvedClientSecret,
      refresh_token: tokenState.refreshToken,
      grant_type: 'refresh_token'
    })
  });

  if (!response.ok) {
    let errText = '';
    try { errText = await response.text(); } catch (_) {}
    throw new Error(`Failed to refresh Strava token (HTTP ${response.status}: ${errText.substring(0, 100)})`);
  }

  const data = await response.json();
  const athleteName = data.athlete
    ? (`${data.athlete.firstname || ''} ${data.athlete.lastname || ''}`.trim() || tokenState.athleteName || '')
    : (tokenState.athleteName || '');

  return {
    ...tokenState,
    clientId: resolvedClientId,
    clientSecret: resolvedClientSecret,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: data.expires_at,
    athlete: data.athlete !== undefined ? data.athlete : tokenState.athlete,
    ...(athleteName ? { athleteName } : {})
  };
}

/**
 * Fetches athlete activities from Strava within an epoch timestamp range.
 *
 * @param {string} accessToken
 * @param {Object} [options={}]
 * @param {number} [options.before]
 * @param {number} [options.after]
 * @param {number} [options.perPage=50]
 * @param {number} [options.page=1]
 * @returns {Promise<Array<Object>>}
 */
export async function fetchStravaActivities(accessToken, options = {}) {
  if (!accessToken) throw new Error('Missing Strava access token');

  const params = new URLSearchParams();
  if (options.before) params.set('before', options.before);
  if (options.after) params.set('after', options.after);
  if (options.perPage) params.set('per_page', options.perPage);
  if (options.page) params.set('page', options.page);

  const url = `https://www.strava.com/api/v3/athlete/activities?${params.toString()}`;
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Strava activities (HTTP ${response.status})`);
  }

  return await response.json();
}

/**
 * Fetches stream telemetry (heartrate, time, watts, cadence) for a Strava activity.
 *
 * @param {string|number} activityId
 * @param {string} accessToken
 * @param {string} [keys='heartrate,time']
 * @returns {Promise<Object>}
 */
export async function fetchStravaActivityStreams(activityId, accessToken, keys = 'heartrate,time') {
  if (!activityId) throw new Error('Missing activityId');
  if (!accessToken) throw new Error('Missing accessToken');

  const response = await fetch(`https://www.strava.com/api/v3/activities/${activityId}/streams?keys=${keys}&key_by_type=true`, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Strava activity streams (HTTP ${response.status})`);
  }

  return await response.json();
}

/**
 * Fetches raw lap records for a Strava activity.
 *
 * @param {string|number} activityId
 * @param {string} accessToken
 * @returns {Promise<Array<Object>>}
 */
export async function fetchStravaActivityLaps(activityId, accessToken) {
  if (!activityId) throw new Error('Missing activityId');
  if (!accessToken) throw new Error('Missing accessToken');

  const response = await fetch(`https://www.strava.com/api/v3/activities/${activityId}/laps`, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Strava laps (HTTP ${response.status})`);
  }

  return await response.json();
}

/**
 * Generates an engaging, descriptive title for a workout activity.
 * @param {Object} workout
 * @returns {string}
 */
export function generateStravaActivityTitle(workout) {
  if (!workout) return '⚡ Workout Session';

  let rawText = (workout.notes || workout.aiSummary || '').replace(/^Synced:\s*"/, '').replace(/"$/, '').trim();

  if (rawText) {
    const firstSentence = rawText.split(/[.!?]/)[0].trim();
    const words = firstSentence.split(/\s+/);
    if (words.length >= 3 && words.length <= 7 && firstSentence.length <= 45) {
      return firstSentence;
    }
  }

  const durationMins = Math.round(workout.duration || 30);
  const type = workout.type || 'Hyrox';
  const sufferScore = typeof getWorkoutSufferScore === 'function' ? getWorkoutSufferScore(workout) : workout.sufferScore;

  if (sufferScore && sufferScore > 80) {
    return `🔥 High-Intensity ${durationMins}m ${type} Session`;
  } else if (workout.maxHeartrate && workout.maxHeartrate > 175) {
    return `⚡ Relentless ${durationMins}m ${type} Workout`;
  } else {
    return `⚡ ${type} Performance Session`;
  }
}

/**
 * Formats a comprehensive workout description for posting to Strava.
 * @param {Object} workout
 * @returns {string}
 */
export function formatWorkoutDescriptionForStrava(workout) {
  if (!workout) return '';

  let desc = `⚡ ROXKIT PERFORMANCE SUMMARY\n`;
  desc += `━━━━━━━━━━━━━━━━━━━━━━\n`;

  let summaryText = workout.aiSummary || workout.notes;
  if (summaryText) {
    summaryText = summaryText.replace(/^Synced:\s*"/, '').replace(/"$/, '').trim();
    desc += `${summaryText}\n\n`;
  } else {
    desc += `${workout.type || 'Hyrox'} session completed on ${workout.date || 'today'}.\n\n`;
  }

  desc += `📊 WORKOUT OVERVIEW\n`;
  desc += `• Duration:  ${formatTotalDuration(workout.duration)}\n`;

  const totalDistKm = getWorkoutTotalDistance(workout);
  if (totalDistKm > 0) {
    desc += `• Distance:  ${totalDistKm.toFixed(2)} km\n`;
  }
  if (workout.avgHeartrate) {
    desc += `• Avg HR:    ${workout.avgHeartrate} bpm${workout.maxHeartrate ? ` (Max ${workout.maxHeartrate})` : ''}\n`;
  }
  const sufferScore = typeof getWorkoutSufferScore === 'function' ? getWorkoutSufferScore(workout) : workout.sufferScore;
  if (sufferScore) {
    desc += `• Suffer:    ${sufferScore}\n`;
  }

  if (workout.stages && workout.stages.length > 0) {
    desc += `\n🏃 STATIONS & SPLITS\n`;
    workout.stages.forEach((stage, idx) => {
      const numStr = String(idx + 1).padStart(2, '0');
      let name = stage.stationName || stage.type || `Stage ${idx + 1}`;
      if (name.length > 11) name = name.slice(0, 10) + '…';
      const paddedName = name.padEnd(11, ' ');
      const timeStr = formatStageDuration(stage.durationMin, stage.durationSec);
      const hrStr = stage.avgHr ? ` | ❤️ ${stage.avgHr} bpm` : '';
      desc += `${numStr}. ${paddedName} ⏱ ${timeStr}${hrStr}\n`;
    });
  }

  try {
    const zoneTimes = typeof calculateWorkoutZoneSeconds === 'function' ? calculateWorkoutZoneSeconds(workout) : null;
    if (zoneTimes) {
      const totalZoneSecs = zoneTimes.reduce((a, b) => a + (b || 0), 0);
      if (totalZoneSecs > 0) {
        desc += `\n📈 HR ZONES\n`;
        const zoneLabels = ['Z1 Recov', 'Z2 Endure', 'Z3 Tempo ', 'Z4 Thresh', 'Z5 Max   '];
        const zoneIcons = ['🟦', '🟩', '🟨', '🟧', '🟥'];
        zoneTimes.forEach((secs, idx) => {
          if (secs > 0) {
            const pct = Math.round((secs / totalZoneSecs) * 100);
            const mins = Math.round(secs / 60);
            desc += `${zoneIcons[idx]} ${zoneLabels[idx]}: ${pct}% (${mins}m)\n`;
          }
        });
      }
    }
  } catch (e) {
    console.error('Error formatting HR zones for Strava:', e);
  }

  desc += `\n🚀 Tracked with RoxKit\n`;
  desc += `https://roxkit.ndclabs.app\n`;
  return desc;
}

/**
 * Formats description for companion running activity on Strava.
 * @param {Object} workout
 * @returns {string}
 */
export function formatRunWorkoutDescriptionForStrava(workout) {
  if (!workout) return '';

  let desc = `🏃 ROXKIT RUNNING SESSION\n`;
  desc += `━━━━━━━━━━━━━━━━━━━━━━\n`;

  const totalDistKm = getWorkoutTotalDistance(workout);
  const totalDurationMin = workout.duration || 30;

  desc += `📊 RUN SUMMARY\n`;
  desc += `• Distance:  ${totalDistKm.toFixed(2)} km\n`;
  desc += `• Duration:  ${formatTotalDuration(totalDurationMin)}\n`;

  if (totalDistKm > 0 && totalDurationMin > 0) {
    const totalPaceSecs = Math.round((totalDurationMin / totalDistKm) * 60);
    const paceMins = Math.floor(totalPaceSecs / 60);
    const paceSecs = totalPaceSecs % 60;
    desc += `• Avg Pace:  ${paceMins}:${String(paceSecs).padStart(2, '0')} /km\n`;
  }

  if (workout.avgHeartrate) {
    desc += `• Avg HR:    ${workout.avgHeartrate} bpm${workout.maxHeartrate ? ` (Max ${workout.maxHeartrate})` : ''}\n`;
  }

  const sufferScore = typeof getWorkoutSufferScore === 'function' ? getWorkoutSufferScore(workout) : workout.sufferScore;
  if (sufferScore) {
    desc += `• Suffer:    ${sufferScore}\n`;
  }

  if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
    const runStages = workout.stages.filter(s => {
      const typeLower = (s.type || '').toLowerCase();
      const nameLower = (s.stationName || '').toLowerCase();
      return (typeLower === 'run' || typeLower === 'cardio' || typeLower === 'roxzone' || nameLower.includes('rox')) && typeLower !== 'rest';
    });

    if (runStages.length > 0) {
      desc += `\n🏃 RUNNING SPLITS\n`;
      runStages.forEach((stage, idx) => {
        const numStr = String(idx + 1).padStart(2, '0');
        let name = stage.stationName || stage.type || `Run ${idx + 1}`;
        if (name.length > 11) name = name.slice(0, 10) + '…';
        const paddedName = name.padEnd(11, ' ');
        const timeStr = formatStageDuration(stage.durationMin, stage.durationSec);
        let distKm = 0;
        const rawDist = Number(stage.distance ?? stage.distance_meters ?? 0);
        if (Number.isFinite(rawDist) && rawDist > 0) {
          distKm = rawDist > 50 ? (rawDist / 1000) : rawDist;
        }
        const distStr = distKm > 0 ? ` | ${distKm.toFixed(2)} km` : ' | 0m';
        const hrStr = stage.avgHr ? ` | ❤️ ${stage.avgHr} bpm` : '';
        desc += `${numStr}. ${paddedName} ⏱ ${timeStr}${distStr}${hrStr}\n`;
      });
    }
  }

  desc += `\n🚀 Tracked with RoxKit\n`;
  desc += `https://roxkit.ndclabs.app\n`;
  return desc;
}

/**
 * Posts a companion Run activity to Strava via /api/v3/activities.
 *
 * @param {Object} workout
 * @param {Object} [options={}]
 * @param {string} [options.accessToken]
 * @returns {Promise<string|number|null>} Created activity ID or null
 */
export async function postRunActivityToStrava(workout, options = {}) {
  const accessToken = options.accessToken || (store && store.strava ? store.strava.accessToken : (typeof state !== 'undefined' && state.strava ? state.strava.accessToken : null));
  if (!accessToken) {
    throw new Error('Strava accessToken is missing');
  }

  const totalDistKm = getWorkoutTotalDistance(workout);
  if (!totalDistKm || totalDistKm <= 0) {
    return null;
  }

  const baseTitle = generateStravaActivityTitle(workout);
  const runActivityName = `${baseTitle} (Run)`;
  const runDescription = formatRunWorkoutDescriptionForStrava(workout);
  const totalSecs = Math.max(60, Math.round((workout.duration || 30) * 60));
  const distMeters = Math.round(totalDistKm * 1000);
  const startDate = new Date(workout.timestamp || Date.now());
  const startIso = startDate.toISOString();

  // If companion run activity already exists on Strava, update via PUT
  if (workout.stravaRunId) {
    try {
      const updateResp = await fetch(`https://www.strava.com/api/v3/activities/${workout.stravaRunId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: runActivityName,
          description: runDescription,
          sport_type: 'Run',
          type: 'Run'
        })
      });
      if (updateResp.ok) {
        return workout.stravaRunId;
      }
    } catch (err) {
      console.warn('[Strava Run Activity] PUT exception:', err);
    }
  }

  const createResp = await fetch('https://www.strava.com/api/v3/activities', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: runActivityName,
      type: 'Run',
      sport_type: 'Run',
      start_date_local: startIso,
      elapsed_time: totalSecs,
      distance: distMeters,
      description: runDescription,
      trainer: workout.trainer ? 1 : 0
    })
  });

  if (!createResp.ok) {
    let errDetail = 'Failed to create companion running activity on Strava';
    try {
      const errData = await createResp.json();
      if (errData.message) errDetail = errData.message;
      if (errData.error) errDetail = errData.error;
      if (errData.errors) errDetail += ' - ' + JSON.stringify(errData.errors);
    } catch (_) {
      errDetail = `HTTP ${createResp.status}: ${createResp.statusText || ''}`.trim();
    }
    console.error(`[Strava Run Activity Error]: ${errDetail}`);
    if (typeof options.onWarning === 'function') {
      options.onWarning(`Note: Primary workout posted, but companion Run failed: ${errDetail}`);
    } else if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast(`Note: Primary workout posted, but companion Run failed: ${errDetail}`, 'warning', 4000);
    }
    return null;
  }

  const createdAct = await createResp.json();
  workout.stravaRunId = createdAct.id;
  return createdAct.id;
}

/**
 * Posts or updates a workout on Strava using TCX multipart upload or direct activity update.
 *
 * @param {Object} workout
 * @param {Object} [options={}]
 * @returns {Promise<Object>} Upload/Update result
 */
export async function postWorkoutToStrava(workout, tokenOrOptions = {}, maybeOptions = {}) {
  const options = typeof tokenOrOptions === 'string'
    ? { ...maybeOptions, accessToken: tokenOrOptions }
    : (tokenOrOptions || {});
  const accessToken = options.accessToken || (store && store.strava ? store.strava.accessToken : (typeof state !== 'undefined' && state.strava ? state.strava.accessToken : null));
  if (!accessToken) {
    throw new Error('Strava account is not connected. Please connect it in Settings.');
  }

  const description = formatWorkoutDescriptionForStrava(workout);
  const activityName = generateStravaActivityTitle(workout);

  // If activity exists on Strava (and not force-reposting), update title & description via PUT endpoint directly (falling back to TCX upload if PUT fails)
  if (!options.forceRepost && workout.stravaId) {
    const updateResp = await fetch(`https://www.strava.com/api/v3/activities/${workout.stravaId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: activityName,
        description: description
      })
    });

    if (updateResp.ok) {
      let runId = workout.stravaRunId || null;
      if (getWorkoutTotalDistance(workout) > 0) {
        if (typeof options.onProgress === 'function') {
          options.onProgress('⏳ Syncing Run...');
        }
        runId = await postRunActivityToStrava(workout, { ...options, accessToken });
      }
      return {
        success: true,
        activityId: workout.stravaId,
        stravaId: String(workout.stravaId),
        stravaRunId: runId ? String(runId) : null,
        status: 'updated',
        updated: true
      };
    }
  }

  // Upload TCX file payload
  const tcxXml = generateTCX(workout);
  let activityType = 'workout';
  if (workout.type === 'Run') activityType = 'run';
  else if (workout.type === 'Cycle') activityType = 'ride';
  else if (workout.type === 'Swim') activityType = 'swim';
  else if (workout.type === 'Walk') activityType = 'walk';

  const tcxBlob = new Blob([tcxXml], { type: 'application/vnd.garmin.tcx+xml' });
  const formData = new FormData();
  formData.append('file', tcxBlob, `roxkit_workout_${workout.id || Date.now()}.tcx`);
  formData.append('data_type', 'tcx');
  formData.append('name', activityName);
  formData.append('description', description);
  formData.append('activity_type', activityType);

  const uploadResp = await fetch('https://www.strava.com/api/v3/uploads', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData
  });

  if (!uploadResp.ok) {
    let errDetail = 'Failed to upload TCX file to Strava';
    try {
      const errData = await uploadResp.json();
      if (errData.message) errDetail = errData.message;
      if (errData.error) errDetail = errData.error;
    } catch (_) {
      errDetail = `HTTP ${uploadResp.status}: ${uploadResp.statusText || ''}`.trim();
    }

    if (uploadResp.status === 401 || errDetail.includes('Authorization Error')) {
      throw new Error('Write permission missing! Please go to Settings → Strava and click "Connect Account" to grant write access.');
    }
    throw new Error(errDetail);
  }

  const uploadResult = await uploadResp.json();
  let activityId = uploadResult.activity_id;
  let wasDuplicateUpdated = false;

  // Poll for processing if activity_id is not immediately available
  const uploadId = uploadResult.id;
  const maxPollAttempts = options.maxPollAttempts !== undefined ? options.maxPollAttempts : 10;
  const pollIntervalMs = options.pollIntervalMs !== undefined ? options.pollIntervalMs : 1500;
  let attempts = 0;
  while (!activityId && attempts < maxPollAttempts && uploadId) {
    attempts++;
    if (typeof options.onProgress === 'function') {
      options.onProgress(`⏳ Processing (${attempts}s)...`);
    }
    await new Promise(res => setTimeout(res, pollIntervalMs));
    try {
      const pollResp = await fetch(`https://www.strava.com/api/v3/uploads/${uploadId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (pollResp.ok) {
        const pollData = await pollResp.json();
        if (pollData.activity_id) {
          activityId = pollData.activity_id;
          break;
        }
        if (pollData.error) {
          const dupMatch = String(pollData.error).match(/duplicate of activity\s+(\d+)/i);
          if (dupMatch && dupMatch[1]) {
            activityId = dupMatch[1];
            wasDuplicateUpdated = true;
            await fetch(`https://www.strava.com/api/v3/activities/${activityId}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ name: activityName, description: description })
            });
            break;
          }
          throw new Error(`Strava processing error: ${pollData.error}`);
        }
      }
    } catch (pollErr) {
      if (pollErr && pollErr.message && pollErr.message.startsWith('Strava processing error:')) {
        throw pollErr;
      }
    }
  }

  let runId = workout.stravaRunId || null;
  if (activityId) {
    workout.stravaId = activityId;
    if (getWorkoutTotalDistance(workout) > 0) {
      if (typeof options.onProgress === 'function') {
        options.onProgress('⏳ Syncing Run...');
      }
      runId = await postRunActivityToStrava(workout, { ...options, accessToken });
    }
  } else if (getWorkoutTotalDistance(workout) > 0) {
    runId = await postRunActivityToStrava(workout, { ...options, accessToken });
  }

  return {
    success: true,
    uploadId: uploadResult.id,
    activityId: activityId || null,
    stravaId: activityId ? String(activityId) : null,
    stravaRunId: runId ? String(runId) : null,
    status: wasDuplicateUpdated ? 'duplicate_updated' : (activityId ? 'created' : 'processing'),
    updated: wasDuplicateUpdated
  };
}
